import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { ExpandMore, Lock } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const DgcaBaselineView = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [baseline, setBaseline] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  const fetchBaseline = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/dgca-profile/baseline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBaseline(response.data);
      setIsCustom(response.data.isCustom || false);
      setError('');
    } catch (error) {
      console.error('Error fetching baseline configuration:', error);
      setError('Failed to load baseline configuration. Please try again.');
      showToast('Failed to load baseline configuration', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchBaseline();
  }, [fetchBaseline]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !baseline) {
    return (
      <Box>
        <Alert severity="error">{error || 'Failed to load baseline configuration'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4">DGCA Baseline Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Current baseline DGCA regulatory limits. These values serve as the safety floor for all compliance checks.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Lock fontSize="small" color="disabled" />
          <Chip label="Read-Only" size="small" color="default" />
        </Box>
      </Box>

      {isCustom && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Custom baseline configuration is active. These values override the system defaults.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This is a read-only view of the baseline configuration. Only SUPER_ADMIN can modify these values.
          To make changes, contact your system administrator or access the baseline management page.
        </Typography>
      </Alert>

      {/* Duty Time / FDP Rules */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Duty Time / FDP Rules</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Daily FDP Hours"
                type="number"
                value={baseline.maxDailyFDPHours}
                InputProps={{ readOnly: true }}
                helperText="Maximum daily Flight Duty Period hours (CAR Section 7, Series J)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Approach Warning Threshold Hours"
                type="number"
                value={baseline.approachWarningThresholdHours}
                InputProps={{ readOnly: true }}
                helperText="Warning threshold before FDP limit"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allow FDP Extensions"
                value={baseline.allowExtensions ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J, Part III"
              />
            </Grid>
            {baseline.allowExtensions && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max FDP Extension Hours"
                    type="number"
                    value={baseline.maxFdpExtensionHours}
                    InputProps={{ readOnly: true }}
                    helperText="Maximum FDP extension under Part III"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Flight Time Extension Hours"
                    type="number"
                    value={baseline.maxFlightTimeExtensionHours}
                    InputProps={{ readOnly: true }}
                    helperText="Maximum flight time extension under Part III"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rest Compensation for 1h Extension"
                    type="number"
                    value={baseline.restCompForExtension1h}
                    InputProps={{ readOnly: true }}
                    helperText="Additional rest hours for 1h extension"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rest Compensation for 2h Extension"
                    type="number"
                    value={baseline.restCompForExtension2h}
                    InputProps={{ readOnly: true }}
                    helperText="Additional rest hours for 2h extension"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Rest Period Rules */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Rest Period Rules</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Rest Hours (Standard)"
                type="number"
                value={baseline.minRestHoursStandard}
                InputProps={{ readOnly: true }}
                helperText="Standard minimum rest period (CAR Section 7, Series J)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Rest Hours (1-3 Time Zones)"
                type="number"
                value={baseline.minRestHoursTimeZone1to3}
                InputProps={{ readOnly: true }}
                helperText="Rest for 1-3 time zones crossed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Rest Hours (4-7 Time Zones)"
                type="number"
                value={baseline.minRestHoursTimeZone4to7}
                InputProps={{ readOnly: true }}
                helperText="Rest for 4-7 time zones crossed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Rest Hours (More Than 7 Time Zones)"
                type="number"
                value={baseline.minRestHoursTimeZoneMoreThan7}
                InputProps={{ readOnly: true }}
                helperText="Rest for more than 7 time zones crossed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Night Duty Extra Rest Hours"
                type="number"
                value={baseline.nightDutyExtraRestHours}
                InputProps={{ readOnly: true }}
                helperText="Additional rest for night duty"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Night Duty Window Start"
                value={baseline.nightDutyWindowStart}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                helperText="HH:mm format"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Night Duty Window End"
                value={baseline.nightDutyWindowEnd}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                helperText="HH:mm format"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Cumulative Flight Time Limits */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Cumulative Flight Time Limits</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Flight Time (7 Days)"
                type="number"
                value={baseline.maxFlightTime7Days}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - 7 consecutive days"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Flight Time (28 Days)"
                type="number"
                value={baseline.maxFlightTime28Days}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - 28 consecutive days"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Flight Time (Annual)"
                type="number"
                value={baseline.maxFlightTimeAnnual}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Calendar year"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Duty Time (Annual)"
                type="number"
                value={baseline.maxDutyTimeAnnual}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Calendar year"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Weekly Rest Rules */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Weekly Rest Rules</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Consecutive Duty Days"
                type="number"
                value={baseline.maxConsecutiveDutyDays}
                InputProps={{ readOnly: true }}
                helperText="Before weekly rest (CAR Section 7, Series J)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Weekly Rest Hours"
                type="number"
                value={baseline.weeklyRestHours}
                InputProps={{ readOnly: true }}
                helperText="Minimum weekly rest (CAR Section 7, Series J)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Require Two Local Nights"
                value={baseline.requireTwoLocalNights ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Standby / Reserve Rules */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Standby / Reserve Rules</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Standby Counts as Duty"
                value={baseline.standbyCountsAsDuty ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="When enabled, standby time counts as duty time"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Reserve Notice Hours"
                type="number"
                value={baseline.minReserveNoticeHours}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Reserve duty notice"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Roster Publication */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Roster Publication</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Roster Publication Days"
                type="number"
                value={baseline.minRosterPublicationDays}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Minimum advance notice"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Require Weekly Rest Shown"
                value={baseline.requireWeeklyRestShown ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Record Keeping */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Record Keeping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Record Retention (Months)"
                type="number"
                value={baseline.minRecordRetentionMonths}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Minimum retention"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Fatigue Reporting */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Fatigue Reporting</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enable Non-Punitive Reporting"
                value={baseline.enableNonPunitiveReporting ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Require Quarterly Report"
                value={baseline.requireQuarterlyReport ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fatigue Report Retention (Months)"
                type="number"
                value={baseline.fatigueReportRetentionMonths}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J - Retention period"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Training */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Training</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Require Annual Fatigue Training"
                value={baseline.requireAnnualFatigueTraining ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
                helperText="CAR Section 7, Series J"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Training Renewal Period (Months)"
                type="number"
                value={baseline.trainingRenewalPeriodMonths}
                InputProps={{ readOnly: true }}
                helperText="Annual renewal period"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default DgcaBaselineView;

